import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Livecoding #39: Towards an AI that writes JavaScript",
  "description": "",
  "published": "2017-06-05T00:00:00.000Z",
  "redirect_from": ["/blog/livecoding-39-towards-ai-writes-javascript/swizec/7621"],
  "categories": "Livecoding, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><em parentName="p">{`This is a Livecoding Recap – an almost-weekly post about interesting things discovered while livecoding. Usually shorter than 500 words. Often with pictures. Livecoding happens almost `}<strong parentName="em">{`every Sunday at 2pm PDT`}</strong>{` on multiple channels. You should follow `}<a parentName="em" {...{
          "href": "https://www.youtube.com/channel/UCoyHgaeLLI7Knp7LDHOwZMw"
        }}>{`My Youtube`}</a>{` channel to catch me live.`}</em></p>
    <p>{`Sound on YouTube was borked yesterday. I sounded like Mr. Robot. It all worked well on LiveEdu, but they didn't save the video it looks like. ¯`}{`\\`}{`_`}{`(ツ)`}{`_`}{`/¯`}</p>
    <lite-youtube {...{
      "videoid": "82yCEYf2q3A",
      "videostartat": "0"
    }}></lite-youtube>
    <p>{`Anyway, we made some progress from `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/livecoding-38-faux-ai-writes-javascript/swizec/7607"
      }}>{`last time`}</a>{`. Our evolutionary approach still gets stuck in a local maximum after just a few iterations, but it no longer gets stuck on a single solution.`}</p>
    <p>{`So even though the fitness score is, say, `}<inlineCode parentName="p">{`0.4545`}</inlineCode>{` and never changes, the result itself keeps changing. We achieved that by improving the pairing function.`}</p>
    <p>{`Our algorithm can now choose between two different pairing functions. Either the top 50% of the population breeds pairwise, or we take the top 30 specimens and breed each specimen with all others in the top 30.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const PAIRING_STRATEGIES = {
  top_half_pairs: (population) =>
    _.chunk(_.take(population, population.length / 2), 2),
  top_30_superset: (population) => {
    const top30 = _.take(population, 30);

    return _.flatten(top30.map((A) => top30.map((B) => [A, B])));
  },
};
`}</code></pre>
    <p>{`This creates a lot of thrashing at the top, and it also makes our code faster to run. Thrashing is what we wanted to achieve, but unfortunately, it didn't have the effect on fitness scores that we hoped for.`}</p>
    <p>{`Observe:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`16
{ fitness: 0.45454545454545453,
  code: '*15+',
  fitnessLast: 0.45454545454545453,
  codeLast: '4584',
  size: 200 }
17
{ fitness: 0.45454545454545453,
  code: '06*0',
  fitnessLast: 0.45454545454545453,
  codeLast: '4584',
  size: 200 }
18
{ fitness: 0.45454545454545453,
  code: '686.+9',
  fitnessLast: 0.45454545454545453,
  codeLast: '4584',
  size: 200 }
19
{ fitness: 0.45454545454545453,
  code: '86.+',
  fitnessLast: 0.45454545454545453,
  codeLast: '4584',
  size: 200 }
20
{ fitness: 0.45454545454545453,
  code: '28+',
  fitnessLast: 0.45454545454545453,
  codeLast: '0+*-/',
  size: 200 }
21
{ fitness: 0.45454545454545453,
  code: '800406',
  fitnessLast: 0.45454545454545453,
  codeLast: '092-092-',
  size: 200 }
22
{ fitness: 0.45454545454545453,
  code: '00406*',
  fitnessLast: 0.45454545454545453,
  codeLast: '092-092-',
  size: 200 }
`}</code></pre>
    <p>{`We achieved total randomness with no convergence. This is kind of pointless, but at least the simplified character set produces more syntactically correct solutions.`}</p>
    <h2 {...{
      "id": "a-glimmer-of-hope"
    }}>{`A glimmer of hope`}</h2>
    <p>{`Towards the end of my livecoding session, we found a glimmer of hope. Using Prettier.`}</p>
    <p><a parentName="p" {...{
        "href": "https://github.com/prettier/prettier"
      }}>{`Prettier`}</a>{` can take a JavaScript AST and convert it into plain JavaScript code. This means we can go 1-up on abstraction levels and still produce readable code in the end.`}</p>
    <p>{`Prettier also pointed us towards `}<a parentName="p" {...{
        "href": "https://github.com/babel/babylon"
      }}>{`Babylon`}</a>{`, which is a JavaScript parser that takes code and turns it into an AST. This means we can take any syntactically valid code and turn it into an AST.`}</p>
    <p>{`ASTs, by the way, are `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Abstract_syntax_tree"
      }}>{`abstract syntax trees`}</a>{`. They're how computers read code.`}</p>
    <p>{`What's left now is to figure out:`}</p>
    <ol>
      <li parentName="ol">{`How to generate an initial population of ASTs`}</li>
      <li parentName="ol">{`How to breed and mutate ASTs`}</li>
    </ol>
    <p>{`🤔`}</p>
    <p>{`You can see the `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/faux-js-ai"
      }}>{`full current code on Github`}</a>{`. Not too useful yet unless you're reading months from now. Github keeps links looking the same :)`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      